import { createFileRoute, Link, useNavigate } from '@tanstack/react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import {
  CalendarClock,
  CheckCircle2,
  ClipboardList,
  Compass,
  GraduationCap,
  Hourglass,
  Mail,
  PartyPopper,
  ScrollText,
  Send,
  ShieldX,
} from 'lucide-react';
import { apiRequest, ApiError } from '../lib/api';
import { useToast } from '../lib/toast';
import { useAuthStore } from '../lib/auth-store';
import { Button } from '../components/ui/Button';
import { Skeleton } from '../components/ui/Skeleton';
import { GatedSection } from '../components/shared/GatedSection';
import {
  ApplicationTimeline,
  StageProgressBar,
  type CandidateStatusResponse,
  type PipelineStage,
} from '../components/hiring';
import {
  LandingCard,
  LandingPage,
} from '../components/portal-shell';

export const Route = createFileRoute('/_authed/candidate/status')({
  component: CandidateStatusPage,
});

function CandidateStatusPage() {
  const user = useAuthStore((s) => s.user);
  const navigate = useNavigate();
  const { toast } = useToast();

  const statusQuery = useQuery({
    queryKey: ['me', 'hiring', 'status'] as const,
    queryFn: ({ signal }) =>
      apiRequest<CandidateStatusResponse>('/api/me/hiring/status', { signal }),
    retry: (failureCount, err) => {
      if (err instanceof ApiError && (err.status === 404 || err.status === 403)) {
        return false;
      }
      return failureCount < 2;
    },
  });

  const acceptOnboarding = useMutation({
    mutationFn: () =>
      apiRequest<{ ok: true }>('/api/me/hiring/onboard/accept', {
        method: 'POST',
        body: {},
      }),
    onSuccess: () => {
      toast('Welcome to Babul Ilm! Please set your password.', {
        variant: 'success',
      });
      void navigate({ to: '/change-password' });
    },
    onError: (err: unknown) =>
      toast(err instanceof ApiError ? err.message : 'Could not accept.', {
        variant: 'error',
      }),
  });

  return (
    <LandingPage
      title={`Assalamu alaikum, ${user?.displayName ?? 'candidate'}`}
      meta="Your application"
      intro="Here's where you are in the hiring journey. We'll email you the moment something changes."
    >
      <GatedSection
        error={statusQuery.error}
        title="My application"
        description="Once the hiring module is enabled by the academy, your application status will appear here."
        onRetry={() => statusQuery.refetch()}
      >
        {statusQuery.isLoading || !statusQuery.data ? (
          <div className="space-y-4">
            <Skeleton className="h-24 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <CandidateStatusBody
            data={statusQuery.data}
            isAcceptingOnboard={acceptOnboarding.isPending}
            onAcceptOnboard={() => acceptOnboarding.mutate()}
          />
        )}
      </GatedSection>
    </LandingPage>
  );
}

interface CandidateStatusBodyProps {
  data: CandidateStatusResponse;
  isAcceptingOnboard: boolean;
  onAcceptOnboard: () => void;
}

function CandidateStatusBody({
  data,
  isAcceptingOnboard,
  onAcceptOnboard,
}: CandidateStatusBodyProps) {
  const stage = data.application.pipelineStage;
  const hasOpenAptitude = data.runs.some(
    (r) => r.scope === 'hiring' && (r.status === 'open' || r.status === 'scheduled'),
  );
  const hasOpenRules = data.runs.some(
    (r) => r.scope === 'rules' && (r.status === 'open' || r.status === 'scheduled'),
  );

  return (
    <div className="space-y-5">
      <section className="rounded-xl border border-gold-300/60 bg-gradient-to-br from-gold-50 to-[color:var(--color-bg-elevated)] px-5 py-5 dark:from-gold-700/20">
        <StageProgressBar stage={stage} />
      </section>

      <NextActionCard
        stage={stage}
        hasOpenAptitude={hasOpenAptitude}
        hasOpenRules={hasOpenRules}
        isAcceptingOnboard={isAcceptingOnboard}
        onAcceptOnboard={onAcceptOnboard}
      />

      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <LandingCard title="Recent updates">
          <ApplicationTimeline
            events={data.timeline}
            limit={5}
            oldestFirst={false}
            hideActor
          />
        </LandingCard>
        <LandingCard title="What to expect">
          <p className="py-1.5 text-[13px] border-b border-dashed border-[color:var(--color-border)] text-[color:var(--color-text)]/85">
            <ClipboardList size={14} aria-hidden="true" className="inline -mt-0.5 mr-1.5" />
            Aptitude · 30-minute timed test, one attempt.
          </p>
          <p className="py-1.5 text-[13px] border-b border-dashed border-[color:var(--color-border)] text-[color:var(--color-text)]/85">
            <Mail size={14} aria-hidden="true" className="inline -mt-0.5 mr-1.5" />
            Interview · 30-minute call with the academic team.
          </p>
          <p className="py-1.5 text-[13px] border-b border-dashed border-[color:var(--color-border)] text-[color:var(--color-text)]/85">
            <Compass size={14} aria-hidden="true" className="inline -mt-0.5 mr-1.5" />
            Demos · 3 short live classes on consecutive days.
          </p>
          <p className="py-1.5 text-[13px] text-[color:var(--color-text)]/85">
            <ScrollText size={14} aria-hidden="true" className="inline -mt-0.5 mr-1.5" />
            Rules &amp; regulations test, then welcome aboard.
          </p>
        </LandingCard>
      </div>
    </div>
  );
}

interface NextActionCardProps {
  stage: PipelineStage;
  hasOpenAptitude: boolean;
  hasOpenRules: boolean;
  isAcceptingOnboard: boolean;
  onAcceptOnboard: () => void;
}

function NextActionCard({
  stage,
  hasOpenAptitude,
  hasOpenRules,
  isAcceptingOnboard,
  onAcceptOnboard,
}: NextActionCardProps) {
  switch (stage) {
    case 'applied':
      if (hasOpenAptitude) {
        return (
          <NextActionShell
            icon={<ClipboardList size={28} aria-hidden="true" />}
            title="Your aptitude test is ready"
            body="Once you click Begin, the timer starts and the test cannot be paused. Take it when you're undisturbed."
            action={
              <Link to="/candidate/aptitude">
                <Button icon={<Send size={14} aria-hidden="true" />}>
                  Take the aptitude test
                </Button>
              </Link>
            }
          />
        );
      }
      return (
        <NextActionShell
          icon={<Hourglass size={28} aria-hidden="true" />}
          title="Application under review"
          body="Our academic team is reviewing your application. We'll email you the moment your aptitude test is ready — no action needed from you right now."
        />
      );
    case 'aptitude_passed':
      return (
        <NextActionShell
          icon={<CalendarClock size={28} aria-hidden="true" />}
          title="Time to book your interview"
          body="You passed the aptitude test — congratulations! Pick a slot that works for you and we'll see you online."
          action={
            <Link to="/candidate/interview/book">
              <Button icon={<CalendarClock size={14} aria-hidden="true" />}>
                Choose an interview slot
              </Button>
            </Link>
          }
        />
      );
    case 'interview_booked':
      return (
        <NextActionShell
          icon={<Mail size={28} aria-hidden="true" />}
          title="Your interview is booked"
          body="You'll get an email with the meeting link or location. You can cancel or reschedule from your interview page."
          action={
            <Link to="/candidate/interview/book">
              <Button variant="secondary">View / cancel</Button>
            </Link>
          }
        />
      );
    case 'interview_passed':
      return (
        <NextActionShell
          icon={<Hourglass size={28} aria-hidden="true" />}
          title="Demos are being scheduled"
          body="The academy is lining up three short demo classes for you. We'll email you the schedule."
        />
      );
    case 'demo_scheduled':
    case 'demo_in_progress':
      return (
        <NextActionShell
          icon={<Compass size={28} aria-hidden="true" />}
          title="Your demo classes"
          body="Bring your best — these are three short sample classes with real students. Times and topics are on your demos page."
          action={
            <Link to="/candidate/demos">
              <Button>View demo schedule</Button>
            </Link>
          }
        />
      );
    case 'selected':
      return (
        <NextActionShell
          icon={<ScrollText size={28} aria-hidden="true" />}
          title="You've been selected!"
          body="One last step — read our rules & regulations and take the short rules test."
          action={
            <Link to="/candidate/rules">
              <Button icon={<ScrollText size={14} aria-hidden="true" />}>
                {hasOpenRules ? 'Start rules test' : 'Read the rules'}
              </Button>
            </Link>
          }
        />
      );
    case 'rules_passed':
      return (
        <NextActionShell
          icon={<PartyPopper size={28} aria-hidden="true" />}
          title="Welcome aboard!"
          body="Accept onboarding to activate your teacher account. You'll be asked to set a new password on your next login."
          action={
            <Button
              icon={<CheckCircle2 size={14} aria-hidden="true" />}
              loading={isAcceptingOnboard}
              onClick={onAcceptOnboard}
            >
              Accept onboarding
            </Button>
          }
        />
      );
    case 'onboarded':
      return (
        <NextActionShell
          icon={<GraduationCap size={28} aria-hidden="true" />}
          title="You're a Babul Ilm teacher"
          body="Your account has been promoted. Head to your teacher dashboard to start preparing for your first class."
          action={
            <Link to="/today">
              <Button>Go to teacher dashboard</Button>
            </Link>
          }
        />
      );
    case 'rejected':
    case 'aptitude_failed':
    case 'interview_failed':
    case 'rules_failed':
      return (
        <NextActionShell
          icon={<ShieldX size={28} aria-hidden="true" />}
          title="Thank you for applying"
          body="After careful review, we won't be moving forward with your application this time. We're truly grateful for your interest in joining Babul Ilm and pray your teaching journey continues to flourish."
        />
      );
    default:
      return (
        <NextActionShell
          icon={<Hourglass size={28} aria-hidden="true" />}
          title="Awaiting next step"
          body="We're working on your application. You'll get an email as soon as there's something to do."
        />
      );
  }
}

interface NextActionShellProps {
  icon: React.ReactNode;
  title: string;
  body: string;
  action?: React.ReactNode;
}

function NextActionShell({ icon, title, body, action }: NextActionShellProps) {
  return (
    <section className="flex flex-col gap-3 rounded-xl border border-[color:var(--color-border)] bg-[color:var(--color-bg-elevated)] p-5 sm:flex-row sm:items-start">
      <div className="shrink-0 rounded-full bg-gold-50 p-3 text-gold-700 dark:bg-gold-700/20 dark:text-gold-100">
        {icon}
      </div>
      <div className="flex-1 space-y-2">
        <h2 className="font-display text-lg font-semibold text-[color:var(--color-text)]">
          {title}
        </h2>
        <p className="text-sm text-[color:var(--color-text-muted)]">{body}</p>
        {action ? <div className="pt-1">{action}</div> : null}
      </div>
    </section>
  );
}
